import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  Group,
} from '@/components/_common';
import SelectPartsListModal from '@/components/_common/SelectPartsListModal';
import WrapperLabelPrintingPage from '@/components/_common/WrapperLabelPrintingPage';
import { LabelPrintingRenderCardContentParams } from '@/components/_common/WrapperLabelPrintingPage/types';
import { StockItem } from '@/core/domain/stock/StockItems/StockItems.types';
import { useDisclosure } from '@/hooks';
import formatterPrice from '@/utils/number/formatterPrice';
import {
  Button,
  Checkbox,
  Col,
  Form,
  InputNumber,
  Row,
  Segmented,
  Select,
} from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { FormInstance } from 'rc-field-form';
import { ReactNode, useMemo, useState } from 'react';
import Barcode from 'react-barcode';
import { useHistory } from 'react-router-dom';

interface CustomOptions {
  displayBarcode: boolean;
  displayCodes?: string[];
  displayImage: boolean;
  responsiveImage: boolean;
  imageWidth: number;
  imageHeight: number;
  imagePosition: string;
}

type StockItemKeys = keyof StockItem;

interface LabelParams extends LabelPrintingRenderCardContentParams<StockItem> {
  custom?: CustomOptions;
}

const fieldLabel: Record<string, string> = {
  barcode: 'Cód. Barras',
  manufacturingCode: 'Cód. Fabric.',
  sku: 'Cód. Inter.',
  companyCode: 'Cód. Empre.',
  cost: 'Prç. Custo',
  sellPrice: 'Prç. Venda',
  realCost: 'Custo Real',
  location: 'Local',
};

export default function PartsLabelPrintPage() {
  const [companyId, setCompanyId] = useState<number>();
  const [parts, setParts] = useState<StockItem[]>([]);
  const [modalOpened, modalAction] = useDisclosure();
  const history = useHistory();

  const isBarcodeSemGtin = (barcode: string) => {
    const barcodeReplaced = barcode.toUpperCase().replaceAll(' ', '');
    return !barcode || barcodeReplaced === 'SEMGTIN';
  };

  const renderCardContent = ({ item, custom, layout }: LabelParams) => {
    const displayBarcodeImage = !!custom?.displayBarcode;
    const fontSize = layout?.fontSize || 0;
    const itemBarcode = item.barcode.trim();

    const fields =
      custom?.displayCodes?.reduce((acc, code) => {
        return { ...acc, [code]: item[code as StockItemKeys] };
      }, {} as Partial<StockItem>) || {};

    const formatValue = (key: string, value: unknown) => {
      if (['cost', 'sellPrice', 'realCost'].includes(key)) {
        return formatterPrice(value as number);
      }
      return value as string;
    };

    const renderImage = () => {
      const width = custom?.responsiveImage ? '100%' : custom?.imageWidth;
      const height = custom?.responsiveImage ? 'auto' : custom?.imageHeight;

      if (!custom?.displayImage) return null;

      return (
        <div style={{ height }}>
          {!!item.imageUrl && (
            <img
              src={item.imageUrl}
              alt="part image"
              style={{
                width,
                height: '100%',
                backgroundColor: 'transparent',
                border: 'none',
                objectFit: 'contain',
              }}
            />
          )}
        </div>
      );
    };

    return (
      <>
        {custom?.imagePosition === 'top' && renderImage()}
        <div className="label-print-card-description">
          {item.description.slice(0, layout?.maxLenghtDescription)}
        </div>
        <div className="label-print-card-content">
          {Object.keys(fields).map((key) => (
            <Group
              gap={4}
              position={layout?.align}
              style={{ flexWrap: 'wrap' }}
            >
              <span>{fieldLabel[key]}:</span>
              <span>
                {formatValue(key, fields[key as StockItemKeys] || '')}
              </span>
            </Group>
          ))}
        </div>
        <div>
          {displayBarcodeImage &&
            (isBarcodeSemGtin(itemBarcode) ? (
              'SEM GTIN'
            ) : (
              <Barcode
                value={itemBarcode.toUpperCase()}
                height={fontSize * 1.3}
                margin={0}
                textMargin={2}
                textAlign={layout?.align}
                width={1.7}
                fontSize={fontSize - 2}
              />
            ))}
        </div>
        {custom?.imagePosition === 'bottom' && renderImage()}
      </>
    );
  };

  const shouldUpdate = (render: (form: FormInstance) => ReactNode) => {
    return (
      <Form.Item noStyle style={{ margin: 0 }} shouldUpdate>
        {render}
      </Form.Item>
    );
  };

  const renderExtra = useMemo(() => {
    return (
      <div style={{ padding: 18 }}>
        <div style={{ marginBottom: 12 }}>
          <strong>Dados de impressão</strong>
        </div>

        <Row gutter={[16, 16]}>
          <Col span={24}>
            <CompaniesInfiniteSelectField
              placeholder="Selecionar empresa"
              value={companyId}
              onChange={setCompanyId}
            />
          </Col>

          <Col span={24}>
            <Button
              block
              type="primary"
              disabled={!companyId}
              onClick={modalAction.open}
            >
              Selecionar peças
            </Button>
          </Col>

          <Col span={24}>
            <span>Etiquetas: {parts.length}</span>
          </Col>
        </Row>
      </div>
    );
  }, [companyId, parts]);

  return (
    <>
      <WrapperLabelPrintingPage
        pageTitle="Peças"
        customKey="parts"
        onBack={() => history.push('/app/operational/inventory/parts')}
        data={parts}
        extraCustombar={renderExtra}
        emptyData={renderExtra}
        renderCardContent={(params) => renderCardContent(params as LabelParams)}
        customOptions={[
          {
            component: (
              <FloatingFormItem
                name={['custom', 'displayBarcode']}
                initialValue={true}
                valuePropName="checked"
              >
                <Checkbox>Exibir imagem do código de barras</Checkbox>
              </FloatingFormItem>
            ),
          },
          {
            component: (
              <FloatingFormItem
                label="Campos de exibição"
                name={['custom', 'displayCodes']}
                getValueFromEvent={(values) => {
                  return values.length ? values : undefined;
                }}
              >
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  options={[
                    { value: 'barcode', label: 'Cód. de Barras' },
                    {
                      value: 'sku',
                      label: 'Cód. Interno',
                    },
                    {
                      value: 'manufacturingCode',
                      label: 'Cód. Fabricante',
                    },
                    { value: 'companyCode', label: 'Cód. Empresa' },
                    { value: 'cost', label: 'Preço de Custo' },
                    { value: 'sellPrice', label: 'Preço de Venda' },
                    { value: 'realCost', label: 'Custo Real' },
                    { value: 'location', label: 'Localização' },
                  ]}
                />
              </FloatingFormItem>
            ),
          },
          {
            component: shouldUpdate((form) => (
              <FloatingFormItem
                name={['custom', 'displayImage']}
                initialValue={false}
                valuePropName="checked"
                getValueFromEvent={({ target }: CheckboxChangeEvent) => {
                  if (target.checked) {
                    form.setFieldValue(['custom', 'imageWidth'], 40);
                    form.setFieldValue(['custom', 'imageHeight'], 40);
                  }
                  return target.checked;
                }}
              >
                <Checkbox>Exibir imagem da peça</Checkbox>
              </FloatingFormItem>
            )),
          },
          // {
          //   component: (
          //     <FloatingFormItem
          //       name={['custom', 'responsiveImage']}
          //       initialValue={false}
          //       valuePropName="checked"
          //     >
          //       <Checkbox>Imagem responsiva</Checkbox>
          //     </FloatingFormItem>
          //   ),
          // },
          {
            component: shouldUpdate((form) => (
              <FloatingFormItem
                label="Largura da imagem (mm)"
                name={['custom', 'imageWidth']}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  disabled={!form.getFieldValue(['custom', 'displayImage'])}
                />
              </FloatingFormItem>
            )),
          },
          {
            component: shouldUpdate((form) => (
              <FloatingFormItem
                label="Altura da imagem (mm)"
                name={['custom', 'imageHeight']}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  disabled={!form.getFieldValue(['custom', 'displayImage'])}
                />
              </FloatingFormItem>
            )),
          },
          {
            component: shouldUpdate((form) => (
              <Form.Item
                label="Posição da imagem"
                name={['custom', 'imagePosition']}
                initialValue="top"
              >
                <Segmented
                  style={{ width: '100%' }}
                  disabled={!form.getFieldValue(['custom', 'displayImage'])}
                  options={[
                    { value: 'top', label: 'Superior' },
                    { value: 'bottom', label: 'Inferior' },
                  ]}
                />
              </Form.Item>
            )),
          },
        ]}
      />

      {typeof companyId === 'number' && (
        <SelectPartsListModal
          itemSelectedIds={[]}
          onCallback={(_parts) => {
            setParts(_parts);
          }}
          open={modalOpened}
          onCancel={modalAction.close}
          queryParams={{ companiesIds: [companyId] }}
        />
      )}
    </>
  );
}
